const DEFAULT_CONCURRENCY_LIMIT = 6;
const projectLocks = new Set();

const ensureString = (value) => (typeof value === "string" ? value.trim() : "");

const escapeSelector = (value) => {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
};

const parseCreatedReportShortGuid = (response) => {
  const data = response?.data;
  if (typeof data === "string" && data.trim() !== "") return data.trim();

  if (typeof data?.shortGuid === "string" && data.shortGuid.trim() !== "") {
    return data.shortGuid.trim();
  }

  if (typeof data?.reportShortGuid === "string" && data.reportShortGuid.trim() !== "") {
    return data.reportShortGuid.trim();
  }

  throw new Error("Create report response does not contain reportShortGuid.");
};

const replacePlaceholderWithReportTag = (htmlContent, placeholderToken, reportShortGuid) => {
  if (!placeholderToken) return htmlContent;
  const reportTag = `report-${reportShortGuid}`;
  return htmlContent.split(placeholderToken).join(reportTag);
};

const removeReportNodesFromHtml = (htmlContent, reportShortGuid) => {
  if (typeof DOMParser === "undefined") return htmlContent;

  const reportTag = `report-${reportShortGuid}`;
  const escapedTag = escapeSelector(reportTag);
  const parser = new DOMParser();
  const document = parser.parseFromString(htmlContent, "text/html");

  const nodes = document.querySelectorAll(`.${escapedTag}, #${escapedTag}`);
  nodes.forEach((node) => node.remove());

  return document.documentElement.outerHTML;
};

const runWithConcurrencyLimit = (items, limit, worker) => {
  const safeLimit = Math.max(1, Math.min(limit, items.length));
  const results = new Array(items.length);
  let nextIndex = 0;
  let firstError = null;

  const runNext = () => {
    if (firstError != null) return Promise.resolve();

    const currentIndex = nextIndex;
    nextIndex += 1;

    if (currentIndex >= items.length) return Promise.resolve();

    return Promise.resolve()
      .then(() => worker(items[currentIndex], currentIndex))
      .then((result) => {
        results[currentIndex] = result;
      })
      .catch((error) => {
        firstError = error;
      })
      .then(runNext);
  };

  return Promise.all(Array.from({ length: safeLimit }, runNext)).then(() => {
    if (firstError != null) throw firstError;
    return results;
  });
};

const validateOperation = (operation) => {
  const type = ensureString(operation?.type).toLowerCase();
  if (!["create", "delete"].includes(type)) {
    throw new Error("Operation type must be either 'create' or 'delete'.");
  }

  if (type === "create" && typeof operation?.request !== "object") {
    throw new Error("Create operation must include a request payload.");
  }

  if (type === "delete" && ensureString(operation?.reportShortGuid) === "") {
    throw new Error("Delete operation must include reportShortGuid.");
  }
};

const applyOperationResultToHtml = (htmlContent, result) => {
  if (result.type === "create") {
    if (typeof result.applyToHtml === "function") {
      return result.applyToHtml(htmlContent, result.reportShortGuid);
    }

    return replacePlaceholderWithReportTag(
      htmlContent,
      ensureString(result.placeholderToken),
      result.reportShortGuid
    );
  }

  if (typeof result.applyToHtml === "function") {
    return result.applyToHtml(htmlContent, result.reportShortGuid);
  }

  return removeReportNodesFromHtml(htmlContent, result.reportShortGuid);
};

/**
 * Bulk report create/delete orchestration:
 * - runs report create/delete with bounded concurrency
 * - uses skipHtmlUpdate=true for report API calls
 * - uploads HTML exactly once at the end
 *
 * @param {Object} params
 * @param {string} params.projectShortGuid
 * @param {string} params.initialHtmlContent
 * @param {Array<Object>} params.operations
 * @param {Function} params.createReport
 * @param {Function} params.deleteReport
 * @param {Function} params.uploadHtml
 * @param {number} [params.concurrencyLimit=6]
 * @param {boolean} [params.skipFullReconciliation=true]
 * @param {Object} [params.diff]
 * @returns {Promise<{results: Array<Object>, htmlContent: string}>}
 */
export const applyBulkReportChanges = async ({
  projectShortGuid,
  initialHtmlContent,
  operations,
  createReport,
  deleteReport,
  uploadHtml,
  concurrencyLimit = DEFAULT_CONCURRENCY_LIMIT,
  skipFullReconciliation = true,
  diff,
}) => {
  if (ensureString(projectShortGuid) === "") {
    throw new Error("projectShortGuid is required.");
  }

  if (typeof initialHtmlContent !== "string") {
    throw new Error("initialHtmlContent must be a string.");
  }

  if (!Array.isArray(operations) || operations.length === 0) {
    throw new Error("operations must be a non-empty array.");
  }

  if (typeof createReport !== "function") {
    throw new Error("createReport function is required.");
  }

  if (typeof deleteReport !== "function") {
    throw new Error("deleteReport function is required.");
  }

  if (typeof uploadHtml !== "function") {
    throw new Error("uploadHtml function is required.");
  }

  if (projectLocks.has(projectShortGuid)) {
    throw new Error(`Bulk report change is already running for project ${projectShortGuid}.`);
  }

  operations.forEach(validateOperation);
  projectLocks.add(projectShortGuid);

  try {
    const operationResults = await runWithConcurrencyLimit(
      operations,
      Number.isFinite(concurrencyLimit) ? Number(concurrencyLimit) : DEFAULT_CONCURRENCY_LIMIT,
      async (operation) => {
        const type = ensureString(operation.type).toLowerCase();

        if (type === "create") {
          const response = await createReport({
            ...operation.request,
            skipHtmlUpdate: true,
          });

          return {
            type,
            reportShortGuid: parseCreatedReportShortGuid(response),
            placeholderToken: operation.placeholderToken,
            applyToHtml: operation.applyToHtml,
          };
        }

        const reportShortGuid = ensureString(operation.reportShortGuid);
        await deleteReport({
          reportShortGuid,
          skipHtmlUpdate: true,
        });

        return {
          type,
          reportShortGuid,
          applyToHtml: operation.applyToHtml,
        };
      }
    );

    const htmlContent = operationResults.reduce(applyOperationResultToHtml, initialHtmlContent);

    await uploadHtml(projectShortGuid, {
      htmlContent,
      ...(diff ? { diff } : {}),
      skipFullReconciliation,
    });

    return {
      results: operationResults,
      htmlContent,
    };
  } finally {
    projectLocks.delete(projectShortGuid);
  }
};

export const clearBulkReportChangeLock = (projectShortGuid) => {
  projectLocks.delete(projectShortGuid);
};
